import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { resources } from '../../core/services/resources';
import { useActiveProject } from '../../core/contexts/ProjectContext';
import { usePermissions } from '../hooks/usePermissions';
import { formatEntityTypeLabel } from './entityTypeLabel';

/**
 * Sidebar list of a project's ProjectManagedEntityType rows — entity types
 * curated as "managed project knowledge". Each row links to a dedicated view
 * of every project entity of that type; the last row (edit permission only)
 * is the "Add entity type" control, sourced from whatever types have
 * actually been extracted for the project so far (same pool as the "Main
 * Entities" block's Add control on the project dashboard).
 */
export function ManagedKnowledgeList() {
  const { activeProject } = useActiveProject();
  const { canEdit } = usePermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const projectId = activeProject?.id;
  const managedApi = projectId != null ? resources.projectManagedEntityTypesFor(projectId) : null;

  const managedQuery = useQuery({
    queryKey: ['project-managed-entity-types', projectId],
    queryFn: () => managedApi!.list({ pageSize: 100, sort: 'order,id' }),
    enabled: projectId != null,
  });
  const managedTypes = managedQuery.data?.data ?? [];

  const entityTypesQuery = useQuery({
    queryKey: ['project-entity-types', projectId],
    queryFn: () => resources.getProjectEntityTypes(projectId!),
    enabled: projectId != null && canEdit,
  });

  const managedEntityTypeNames = managedTypes.map((t) => t.entityType);
  const entityCountsQuery = useQuery({
    queryKey: ['managed-knowledge-entity-counts', projectId, managedEntityTypeNames],
    queryFn: () =>
      resources.entities.list({
        filter: { projectId: [projectId!], entityType: managedEntityTypeNames },
        pageSize: 2000,
      }),
    enabled: projectId != null && managedEntityTypeNames.length > 0,
  });

  // One row per entity, so this is a plain count per type.
  const entityCountByType = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of entityCountsQuery.data?.data ?? []) {
      counts.set(row.entityType, (counts.get(row.entityType) ?? 0) + 1);
    }
    return counts;
  }, [entityCountsQuery.data]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['project-managed-entity-types', projectId] });

  const addType = useMutation({
    mutationFn: (entityType: string) => {
      const nextOrder = managedTypes.reduce((max, t) => Math.max(max, t.order), -1) + 1;
      return managedApi!.create({ entityType, order: nextOrder });
    },
    onSuccess: invalidate,
  });
  const removeType = useMutation({
    mutationFn: (id: number) => managedApi!.remove(id),
    onSuccess: invalidate,
  });

  // Reorders a row by swapping `order` with the adjacent row (by current
  // display order) — same swap pattern used for dashboard card reordering.
  const moveType = useMutation({
    mutationFn: async ({ id, direction }: { id: number; direction: 'up' | 'down' }): Promise<void> => {
      const sorted = [...managedTypes].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex((t) => t.id === id);
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (index === -1 || swapIndex < 0 || swapIndex >= sorted.length) return;
      const current = sorted[index];
      const neighbor = sorted[swapIndex];
      await Promise.all([
        managedApi!.update(current.id, { order: neighbor.order }),
        managedApi!.update(neighbor.id, { order: current.order }),
      ]);
    },
    onSuccess: invalidate,
  });

  if (!activeProject) {
    return (
      <Box sx={{ px: 2.5, py: 0.5 }}>
        <Typography variant="caption" color="text.disabled">
          Select a project to see managed knowledge
        </Typography>
      </Box>
    );
  }

  if (managedQuery.isLoading) {
    return (
      <Box sx={{ px: 2.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  const usedTypes = new Set(managedTypes.map((t) => t.entityType));
  const availableTypes = (entityTypesQuery.data ?? []).filter((t) => !usedTypes.has(t));

  if (managedTypes.length === 0 && !canEdit) {
    return (
      <Box sx={{ px: 2.5, py: 0.5 }}>
        <Typography variant="caption" color="text.disabled">
          No managed entity types yet
        </Typography>
      </Box>
    );
  }

  return (
    <List dense disablePadding sx={{ px: 1 }}>
      {managedTypes.map((row, index) => {
        const to = `/projects/${activeProject.id}/managed-knowledge/${encodeURIComponent(row.entityType)}`;
        const isActive = pathname === to;
        return (
          <ListItemButton
            key={row.id}
            selected={isActive}
            onClick={() => navigate(to)}
            sx={{
              borderRadius: 1,
              py: 0.3,
              '&:hover .managed-type-action-btn': { opacity: 1 },
            }}
          >
            <ListItemIcon sx={{ minWidth: 26 }}>
              <BookmarksOutlinedIcon
                sx={{ fontSize: 15, color: isActive ? 'primary.main' : 'text.secondary' }}
              />
            </ListItemIcon>
            <ListItemText
              primary={formatEntityTypeLabel(row.entityType)}
              primaryTypographyProps={{
                variant: 'body2',
                fontSize: 12,
                fontWeight: isActive ? 600 : 400,
                noWrap: true,
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontSize: 11, color: isActive ? 'primary.main' : 'text.disabled', flexShrink: 0, ml: 0.5 }}
            >
              {entityCountByType.get(row.entityType) ?? 0}
            </Typography>
            {canEdit && (
              <>
                <IconButton
                  size="small"
                  className="managed-type-action-btn"
                  disabled={index === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveType.mutate({ id: row.id, direction: 'up' });
                  }}
                  sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.4 }}
                >
                  <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  className="managed-type-action-btn"
                  disabled={index === managedTypes.length - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveType.mutate({ id: row.id, direction: 'down' });
                  }}
                  sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.4 }}
                >
                  <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  className="managed-type-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeType.mutate(row.id);
                  }}
                  sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.4, ml: 0.5 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </>
            )}
          </ListItemButton>
        );
      })}

      {canEdit && availableTypes.length > 0 && (
        <FormControl size="small" fullWidth sx={{ mt: 0.5, px: 1 }}>
          <Select
            displayEmpty
            value=""
            onChange={(e) => {
              const value = e.target.value;
              if (value) addType.mutate(value);
            }}
            renderValue={() => (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                <AddIcon sx={{ fontSize: 15 }} />
                <Typography variant="caption">Add entity type</Typography>
              </Stack>
            )}
            sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {availableTypes.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                {formatEntityTypeLabel(t)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {managedTypes.length === 0 && availableTypes.length === 0 && canEdit && (
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <Typography variant="caption" color="text.disabled">
            No entity types extracted yet
          </Typography>
        </Box>
      )}
    </List>
  );
}
